import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import ideaStatusGroupsQuery from '@aurora/shared-client/components/ideas/IdeaStatusGroups.query.graphql';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import { dropdownPopperConfig } from '@aurora/shared-client/helpers/ui/PopperJsHelper';
import type { Idea, IdeaStatusDetails } from '@aurora/shared-generated/types/graphql-schema-types';
import type {
  ContextNodeFragment,
  IdeaStatusGroupsQuery,
  IdeaStatusGroupsQueryVariables
} from '@aurora/shared-generated/types/graphql-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import React, { useState } from 'react';
import { Dropdown, useClassNameMapper } from 'react-bootstrap';
import useTranslation from './../../../useTranslation';
import localStyles from './IdeaMessagesStatusFilter.module.pcss';
import IdeaMessagesStatusFilterDropdownItem from './IdeaMessagesStatusFilterDropdownItem';
import type {
  IdeaStatusInformationQuery,
  IdeaStatusInformationQueryVariables
} from '../../../../types/graphql-types';
import ideaStatusPropertiesQuery from '../../../ideas/IdeaStatusInformation.query.graphql';

export type StatusGroup = {
  /**
   * The group key
   */
  groupKey: string;
  /**
   * the group title
   */
  groupTitle: string;
};

interface Props {
  /**
   * The selected status, e.g. through the querystring
   */
  selectedStatus: string;
  /**
   * The node which filtering should be scoped to.
   */
  contextNode: ContextNodeFragment;
  /**
   * Class name(s) to apply to the component element.
   */
  className?: string;
  /**
   * Callback function when a status filter is selected.
   *
   * @callback
   */
  onChange?: (item: IdeaStatusDetails) => void;
  /**
   * The selected status group
   */
  statusGroup?: StatusGroup;
}

/**
 * The idea status filter for the ideas widget filter bar.
 * This will load a dropdown of statuses that contain one or more idea messages.
 *
 * @author rishabh.jivan
 */
const IdeaMessagesStatusFilterDropdown = ({
  selectedStatus,
  className,
  contextNode,
  onChange,
  statusGroup = null
}: Props): React.ReactElement => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const i18n = useTranslation(EndUserComponent.IDEA_MESSAGES_STATUS_FILTER);
  const cx = useClassNameMapper(localStyles);

  const { data: propertiesData, loading: propertiesLoading } = useQueryWithTracing<
    IdeaStatusInformationQuery,
    IdeaStatusInformationQueryVariables
  >(module, ideaStatusPropertiesQuery, {
    variables: {
      id: contextNode.id,
      useIdeaStatusProperties: true
    }
  });

  const isIdeaMessageStatusEnabled = (propertiesData?.coreNode as Idea)?.ideaStatusProperties
    ?.isIdeaStatusEnabled;

  const { data: ideaGroupsResult, loading: statusLoading } = useQueryWithTracing<
    IdeaStatusGroupsQuery,
    IdeaStatusGroupsQueryVariables
  >(module, ideaStatusGroupsQuery, {
    variables: {
      id: contextNode.id,
      useGroupKey: true,
      useDescription: false,
      useLabel: false
    },
    skip: !isIdeaMessageStatusEnabled
  });

  /**
   * Renders individual idea status items in the dropdown
   * @param item
   */
  function renderStatusDropdownMenuItem(item: IdeaStatusDetails): React.ReactElement {
    return (
      <IdeaMessagesStatusFilterDropdownItem
        item={item}
        active={item.statusKey === selectedStatus}
        onStatusSelected={(): void => onChange(item)}
        contextNode={contextNode}
        key={item.statusKey}
      />
    );
  }

  /**
   * Render the idea status dropdown menu with statuses that return one or more idea messages
   */
  function renderStatusDropdown(ideaStatusList): React.ReactElement {
    const ideaStatusMap = new Map(
      ideaStatusList.map(ideaStatus => {
        return [ideaStatus.statusKey, ideaStatus.text];
      })
    );
    const selectedStatusLabel =
      (ideaStatusMap.get(selectedStatus) as string) ?? i18n.formatMessage('allIdeaStatuses');
    return (
      <Dropdown
        className={cx(className, 'lia-g-py-5')}
        data-testid="IdeaMessagesStatusFilter.Dropdown"
        onToggle={isOpen => {
          setDropdownOpen(isOpen);
        }}
        data-menu-open={dropdownOpen}
      >
        <Dropdown.Toggle
          as={Button}
          variant={ButtonVariant.LINK}
          size="sm"
          className={cx('lia-g-dropdown-toggle lia-sort-dropdown')}
          aria-label={selectedStatusLabel}
        >
          {selectedStatusLabel}
        </Dropdown.Toggle>
        <Dropdown.Menu align="right" popperConfig={dropdownPopperConfig}>
          {ideaStatusList.map(ideaStatus => renderStatusDropdownMenuItem(ideaStatus))}
        </Dropdown.Menu>
      </Dropdown>
    );
  }
  if (
    statusLoading ||
    !(ideaGroupsResult?.coreNode as Idea)?.ideaStatusGroups ||
    propertiesLoading ||
    !isIdeaMessageStatusEnabled ||
    i18n.loading
  ) {
    return null;
  }

  let ideaStatusList = [
    {
      statusKey: '',
      text: i18n.formatMessage('allIdeaStatuses', {
        suffix: statusGroup ? 'group' : 'all',
        groupTitle: statusGroup?.groupTitle
      })
    }
  ];
  for (const group of (ideaGroupsResult.coreNode as Idea).ideaStatusGroups.groups) {
    if (statusGroup && group.groupKey === statusGroup.groupKey) {
      ideaStatusList = [...ideaStatusList, ...group.statuses];
    } else if (!statusGroup) {
      ideaStatusList = [...ideaStatusList, ...group.statuses];
    }
  }
  return <>{renderStatusDropdown(ideaStatusList)}</>;
};

export default IdeaMessagesStatusFilterDropdown;
